<template>
  <span class="inline-flex items-center" v-bind="$attrs">
    <img
      v-if="imageSrc"
      :src="imageSrc"
      :alt="altText"
      :class="['object-contain', shapeClass, sizeClass]"
      loading="lazy"
      decoding="async"
    />
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import currencyMap from '@/components/currency-flag/currency'

type SizeKey = 'sm' | 'md' | 'lg' | 'xl'
type RatioKey = '4x3' | '1x1'
type ShapeKey = 'circle' | 'square'

const props = withDefaults(
  defineProps<{
    currency?: string
    flag?: string
    size?: SizeKey
    ratio?: RatioKey
    shape?: ShapeKey
  }>(),
  {
    flag: '',
    size: 'md',
    ratio: '4x3',
    shape: 'circle',
  },
)

const sizeClassMap: Record<SizeKey, string> = {
  sm: 'w-4 h-4', // 16px
  md: 'w-5 h-5', // 20px
  lg: 'w-6 h-6', // 24px
  xl: 'w-7 h-7', // 28px
}

// 当为方形时，保持原始比例：仅限制宽度，高度自适应
const sizeClassSquareMap: Record<SizeKey, string> = {
  sm: 'w-4 h-auto',
  md: 'w-5 h-auto',
  lg: 'w-6 h-auto',
  xl: 'w-7 h-auto',
}

const resolvedFlagCode = computed(() => {
  if (props.flag && props.flag.trim) return props.flag.trim().toLowerCase()
  const code = props.currency?.trim().toUpperCase()
  if (!code) return ''
  const mapped = (currencyMap as Record<string, string>)[code]
  return (mapped || code).toLowerCase()
})

const imageSrc = computed(() => {
  const code = resolvedFlagCode.value
  if (!code) return ''
  // 直接使用 public 目录下的静态路径，浏览器按需加载
  return `/flag/${code}.png`
})

const sizeClass = computed(() =>
  props.shape === 'circle' ? sizeClassMap[props.size] : sizeClassSquareMap[props.size],
)

const shapeClass = computed(() => (props.shape === 'circle' ? 'rounded-full' : 'rounded-none'))

const altText = computed(() => (props.currency || props.flag || 'flag').toString())
</script>
